import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import { type FunctionComponent, type HTMLAttributes, type PropsWithChildren } from 'react';
import { type ProductOptionFieldsFragment } from '../../../__generated__/graphql-client-types';
import { generateDataSelector } from '../../../helpers/general-helper/general-helper';
import { isApplicable } from '../../../helpers/product-helper/product.helper';
import { type ProductResourceVariantCard } from '../../../types/product.types';
import { DropdownMenu } from '../../common-components/dropdown-menu/dropdown-menu.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import { QuantityInput } from '../../inputs/quantity-input/quantity-input.component';
import { ProductPrice } from '../../product-components/product-price/product-price.component';
import { ProductStock } from '../../product-components/product-stock/product-stock.component';

type ProductDropdownVariantProps = {
	selectedVariant?: ProductResourceVariantCard;
	variants: ProductResourceVariantCard[];
	onVariantSelected: (selected: ProductResourceVariantCard) => void;
};

const ProductDropdownVariantSelector: FunctionComponent<ProductDropdownVariantProps> = ({
	selectedVariant,
	variants,
	onVariantSelected
}) => {
	if (!selectedVariant) {
		return null;
	}

	return (
		<div className="w-100 mt2" data-testid="variant-dropdown-selector">
			<DropdownMenu shouldCloseOnChoice={true} title={selectedVariant.title}>
				{variants.map((variant, index) => (
					<button
						tabIndex={index}
						key={`${variant.title}-${variant.id}-${index}`}
						className="pointer bn bg-transparent pa3 tl w-100"
						onClick={() => onVariantSelected(variant)}>
						{variant.title}
					</button>
				))}
			</DropdownMenu>
		</div>
	);
};

export type ProductSelectionCardProps = {
	variant: ProductResourceVariantCard;
	quantity?: number;
	ElementTag?: keyof Pick<JSX.IntrinsicElements, 'div' | 'li' | 'section'>;
	testId?: string;
	automationHook?: string;
	variantChange?: (variantSelected: ProductResourceVariantCard) => void;
	quantityChange?: (quantity?: number) => void;
	displayModelNumber?: boolean;
} & ProductOptionFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const ProductSelectionCard: FunctionComponent<PropsWithChildren<ProductSelectionCardProps>> = ({
	className = 'w-100 pa3',
	ElementTag = 'div',
	children,
	testId = 'selection-card',
	automationHook = 'product-selection-option',
	quantityChange,
	variantChange,
	quantity = 1,
	variant,
	displayModelNumber = false,
	...option
}) => {
	const { resource, name } = option;
	const { brandName, finishes } = resource || { finishes: [] };
	const defaultClass = `flex flex-row flex-wrap flex-nowrap-ns items-stretch justify-start br2 ba b--theme-grey-light ${className}`;
	const selectedVariant = resource?.finishes.find((finish) => finish.id === variant.id) || resource?.finishes[0];
	const displayVariantName = finishes.length === 1;

	return resource ? (
		<ElementTag
			className={defaultClass}
			data-testid={kebabCase(`${testId}-${name}`)}
			data-automation={generateDataSelector('listitem', kebabCase(`${automationHook}-${variant.id}`))}>
			<div className="center mb2 mr3-ns">
				<CloudinaryImage
					className="w-50 center w4-ns mb2 mr3-ns"
					publicID={variant.image.id}
					description={variant.image.description}
					options={{ width: 128, height: 124 }}
					useDimensions={false}
				/>
			</div>
			<div className="flex flex-row flex-wrap items-stretch min-h-100 w-100">
				<span className="tc2-title theme-black dib mt0 w-100">
					{brandName && <strong className="pr1 f-normal">{brandName}</strong>}
					{option.name}
				</span>
				{displayModelNumber && (
					<div className={'f6 lh-title mb1'}>
						Model: <span className={'font-medium theme-grey'}>{selectedVariant?.modelNumber}</span>
					</div>
				)}
				{isApplicable(variant.title) && (
					<div className={`flex flex-row flex-wrap items-center mt3 w-100`}>
						<span>Finish: </span>
						{displayVariantName && (
							<span className="ml2 font-medium" data-automation="finish-name">
								{variant.title}
							</span>
						)}
						<div className="flex flex-row flex-wrap items-center justify-start">
							{displayVariantName && <span className="theme-emphasis mh1">-</span>}
							<ProductStock count={variant.count} isMadeToOrder={false} brand={brandName || ''} />
						</div>
					</div>
				)}
				{variantChange &&
					(finishes.length > 1 ? (
						<ProductDropdownVariantSelector
							variants={finishes}
							selectedVariant={selectedVariant}
							onVariantSelected={variantChange}
						/>
					) : (
						<div className="dib f5 theme-grey-darker">
							Finish:<span className="font-medium ml1">{finishes[0].title}</span>
						</div>
					))}
				<div className="flex flex-row flex-nowrap items-center w-100 mt1">
					<span>Price: </span>
					<ProductPrice className="ml2" current={variant.price} />
				</div>
				{quantityChange && (
					<div className="mt1 w4">
						<QuantityInput quantity={quantity} setQuantity={quantityChange} automationHook="product-option-quantity" />
					</div>
				)}
				<div className="w-100">{children}</div>
			</div>
		</ElementTag>
	) : null;
};
